import { useCallback, useSyncExternalStore } from "react";
import { useAppContext } from "../context/AppContext";
import { opmStorage, Keys } from "../util/opmStorage";

const FAVORITES_TTL_SECONDS = 60 * 60 * 24 * 90;
const listeners = new Set();
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const EMPTY = [];
let cache = { key: null, ids: EMPTY };

const read = (key) => {
  if (!key) return EMPTY;
  if (cache.key !== key) {
    const stored = opmStorage.getWithTtl(key);
    cache = { key, ids: Array.isArray(stored) ? stored : EMPTY };
  }
  return cache.ids;
};

/**
 * The dishes the guest marked with a heart, by menu item id. They are kept on this phone
 * only (there is no favorites API), per restaurant.
 */
export default function useFavorites() {
  const { guestRestaurantId } = useAppContext();
  const key = guestRestaurantId ? Keys.favorites(guestRestaurantId) : null;
  const ids = useSyncExternalStore(subscribe, () => read(key));

  const isFavorite = useCallback((itemId) => ids.includes(itemId), [ids]);

  const toggleFavorite = useCallback(
    (itemId) => {
      if (!key || itemId == null) return;
      const current = read(key);
      const next = current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId];
      opmStorage.setWithTtl(key, next, FAVORITES_TTL_SECONDS);
      cache = { key, ids: next };
      listeners.forEach((l) => l());
    },
    [key]
  );

  return { favoriteIds: ids, isFavorite, toggleFavorite };
}
